/**
 * BlogClusterView — topic hub at /blog/c/<cluster>/ and
 * /<locale>/blog/c/<cluster>/ (one route family via PublicRoutes' double
 * mount, same as BlogIndexView). The hub layer of the hub-and-spoke blog
 * architecture (docs/plans/landing-v12-strategy.md P0):
 *
 *  - gives 60+ posts a crawlable cluster root (a DA~0 domain has no authority
 *    to waste on flat URLs);
 *  - hands blog→product CTAs a per-persona destination (the exam/list
 *    clusters convert on "bring your own material", not on the extension);
 *  - concentrates internal links so cluster equity flows to the posts.
 *
 * Root hub: en-framed intro, posts grouped by language (BlogIndexView's
 * pattern). Locale hub: locale-framed intro, own-language posts only.
 * Post links are CANONICAL /blog/<slug>/ URLs everywhere (seo-signal-fixes
 * .md rule 1 — variants link their own prefixed URL).
 */
import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { POSTS, BLOG_VARIANTS, CLUSTER_IDS, CLUSTER_LOCALES, CLUSTER_LABEL, BLOG_LANGS, BLOG_LANG_LABEL } from './registry'
import { Footer } from '../landing/Footer'
import { LandingI18nProvider } from '../landing/LandingI18n'
import { usePublicHref } from '../landing/landing-locale'
import { splitLocalePath } from '../../lib/public-locale'

type Page = {
  path: string // href target, no trailing slash
  lang: string
  cluster?: string // variant pages inherit the parent post's cluster
  title: string
  date: string
  description: string
}

function frontmatter(source: string) {
  const block = source.match(/^---\n([\s\S]*?)\n---/)?.[1] ?? ''
  return {
    date: block.match(/^date:\s*(\d{4}-\d{2}-\d{2})/m)?.[1] ?? '',
    description: block.match(/^description:\s*"(.*)"/m)?.[1] ?? '',
  }
}

/** All indexable pages (POSTS + BLOG_VARIANTS), variants tagged with the
 *  parent post's cluster. Newest first — same as BlogIndexView. */
function allPages(): Page[] {
  const pages: Page[] = []
  for (const [path, post] of Object.entries(POSTS)) {
    if (path === '/welcome') continue
    pages.push({ path, ...post, ...frontmatter(post.source) })
  }
  for (const [path, variants] of Object.entries(BLOG_VARIANTS))
    for (const [locale, v] of Object.entries(variants))
      pages.push({
        path: `/${locale}${path}`,
        ...v,
        cluster: POSTS[path]?.cluster,
        ...frontmatter(v.source),
      })
  return pages.sort((a, b) => (a.date < b.date ? 1 : -1))
}

// Hub copy per cluster × frame language. `en` frames the root hub; a locale
// hub uses its own language, falling back to en. Titles double as
// document.title; prerender bakes the SERP description separately.
const CLUSTER_COPY: Record<string, Record<string, { title: string; sub: string }>> = {
  'exam-vocab': {
    en: {
      title: 'Exam vocabulary guides',
      sub: 'The word list is the easy part — every exam publishes one. What decides the score is what happens after you have the list: filtering out what you already know, and scheduling the rest until test day. Guides for TOEIC, IELTS, 수능, JLPT, HSK, and 會考.',
    },
    ko: {
      title: '시험 영어 어휘 가이드',
      sub: '시험마다 단어 목록은 공개되어 있습니다. 점수를 가르는 건 목록을 손에 넣은 다음입니다 — 아는 단어를 걸러내고 남는 것을 시험일까지 복습으로 돌리는 일. 토익·토플·수능·공무원·편입·텝스별 가이드.',
    },
    ja: {
      title: '試験の英単語ガイド',
      sub: '試験ごとに語彙の範囲は決まっています。差がつくのは一覧を手にしてからです — 既知の語を除いて、残りを試験日まで復習に回すこと。TOEIC・英検・中学英単語を級・学年別に整理します。',
    },
    'zh-TW': {
      title: '考試單字指南',
      sub: '每個考試都會公布單字範圍，決定分數的是拿到字表之後的事：濾掉已經會的，把剩下的排程複習到考試當天。會考、多益、KET 的準備指南。',
    },
  },
  'vocab-notebook': {
    en: {
      title: 'Word notebook guides',
      sub: 'A word notebook is a system, not a stack of paper. Which format does the job — paper, book, PDF, app, or cards — and what you filter into it from real material decides whether it survives past page three.',
    },
    ko: {
      title: '영어 단어장 가이드',
      sub: '단어장은 종이 더미가 아니라 시스템입니다. 종이·책·pdf·앱·카드 중 무엇이 그 역할을 감당하는지, 그리고 실제 재료에서 무엇을 걸러 넣을지가 세 페이지를 넘기는 조건입니다.',
    },
    'zh-TW': {
      title: '單字表與單字本指南',
      sub: '下載來的單字表為什麼總是停在前三頁：它解決的是「取得內容」，不是「排程複習」。把字表變成會複習的單字卡的做法，一步步整理。',
    },
  },
  'srs-method': {
    en: {
      title: 'Memorization method guides',
      sub: 'Forgetting is the default; remembering is scheduling. The principles that work — spacing, retrieval, context — are old and well-tested. These guides apply them to vocabulary, day by day.',
    },
    ko: {
      title: '암기법 가이드',
      sub: '잊는 것은 기본값이고, 외우는 것은 스케줄링입니다. 효과가 검증된 원리 — 간격, 인출, 맥락 — 을 영단어에 적용하는 방법을 정리했습니다.',
    },
    ja: {
      title: '覚え方ガイド',
      sub: '忘れるのは初期設定で、覚えることはスケジューリングです。間隔・回想・文脈という検証済みの原理を、英単語に日々適用する方法を整理します。',
    },
  },
  'tool-picking': {
    en: {
      title: 'Choosing your study tools',
      sub: 'Dictionary apps, vocab sites, Anki decks, study apps — the question is rarely "which is best" and usually "which gap in my routine needs filling." Comparisons that start from the job, not the brand.',
    },
    ko: {
      title: '공부 도구 고르기',
      sub: '사전 앱, 단어 사이트, Anki 덱, 학습 앱 — 무엇이 좋냐보다 내 루틴의 어디가 비었는지가 선택의 기준입니다. 브랜드가 아니라 할 일에서 시작하는 비교들.',
    },
    ja: {
      title: 'ツールの選び方',
      sub: '辞書アプリも単語帳アプリもAnkiも、問いは「どれが良いか」ではなく「自分の勉強のどこが空いているか」です。ブランドからではなく、やることから入る比較を整理します。',
    },
  },
  'youtube-capture': {
    en: {
      title: 'Learning English from YouTube',
      sub: 'The words you meet in videos you actually watch are the ones worth keeping. These guides cover the habit — capturing lines as you watch — and the review loop that makes them stay.',
    },
    ko: {
      title: '유튜브 영어 공부 가이드',
      sub: '보던 영상에서 만나는 단어가 남길 가치가 있는 단어입니다. 보다가 문장을 저장하는 습관과, 그것을 남게 하는 복습 루프를 정리했습니다.',
    },
    ja: {
      title: 'YouTube英語ガイド',
      sub: '自分が観ている動画で出会う単語こそ残す価値があります。観ながら行を拾う習慣と、それを定着させる復習の回し方を整理します。',
    },
    'zh-TW': {
      title: 'YouTube 英語指南',
      sub: '自己正在看的影片裡出現的單字,才是值得留下來的單字。一邊看一邊把字句存成單字卡,再用複習讓它留下來的做法。',
    },
  },
  'card-making': {
    en: {
      title: 'Making word cards',
      sub: 'A stack of made cards and a habit of reviewing them are different achievements. What belongs on a card, which format keeps the making cheap enough that it actually happens, and where stacks usually die.',
    },
    ko: {
      title: '단어 카드 만들기',
      sub: '카드를 만들어 쌓아둔 것과 복습하는 습관은 다른 성취입니다. 카드에 무엇을 넣을지, 만드는 비용을 어떻게 줄일지를 정리합니다.',
    },
    ja: {
      title: '単語カードの作り方',
      sub: 'カードを作り溜めることと、復習する習慣は別の達成です。カードに載せるものと、作る手間をどう減らすかを整理します。',
    },
  },
  'language-reactor': {
    en: {
      title: 'Language Reactor guides',
      sub: 'Alternatives to Language Reactor, its Anki export and its limits — and what a capture-plus-review loop looks like when the subtitle tool is also the thing that remembers.',
    },
    ko: {
      title: 'Language Reactor 가이드',
      sub: 'Language Reactor의 대안과 Anki 내보내기의 한계 — 자막 도구가 기억까지 맡는 형태가 어떤 것인지 정리합니다.',
    },
  },
}

/** "All posts" link label per frame language. */
const ALL_POSTS_I18N: Record<string, string> = {
  en: 'All posts',
  ko: '전체 글 보기',
  ja: 'すべての記事',
  'zh-TW': '全部文章',
}

export function BlogClusterView() {
  const { pathname } = useLocation()
  const { locale, path } = splitLocalePath(pathname)
  // /blog/c/<id> — trailing slash tolerated like BlogView's normalization.
  const cluster = path.replace(/\/$/, '').match(/^\/blog\/c\/([a-z-]+)$/)?.[1]

  // Guard BEFORE the provider/body so the body's hooks run unconditionally
  // (rules-of-hooks): unknown clusters and cluster/locale combos without
  // content land on the blog index.
  if (!cluster || !CLUSTER_IDS.includes(cluster) || (locale && !CLUSTER_LOCALES[cluster]?.includes(locale)))
    return <Navigate to={locale ? `/${locale}/blog` : '/blog'} replace />

  return (
    <LandingI18nProvider>
      <BlogClusterBody cluster={cluster} />
    </LandingI18nProvider>
  )
}

function BlogClusterBody({ cluster }: { cluster: string }) {
  const { pathname } = useLocation()
  const href = usePublicHref()
  const { locale } = splitLocalePath(pathname)

  const copy = CLUSTER_COPY[cluster]?.[locale ?? 'en'] ?? CLUSTER_COPY[cluster].en
  const label = (l: string) => CLUSTER_LABEL[cluster]?.[l] ?? CLUSTER_LABEL[cluster].en
  const inCluster = allPages().filter((p) => p.cluster === cluster)

  useEffect(() => {
    document.title = `${copy.title} | Piccard`
  }, [copy])

  // Locale hub: own language only. Root hub: everything, grouped by language.
  const groups = locale
    ? [[BLOG_LANG_LABEL[locale] ?? locale, inCluster.filter((p) => p.lang === locale)]]
    : BLOG_LANGS.map((l) => [BLOG_LANG_LABEL[l], inCluster.filter((p) => p.lang === l)]).filter(
        ([, list]) => (list as Page[]).length > 0,
      )

  return (
    <div className="min-h-screen bg-surface flex flex-col">
      <header className="border-b border-outline-variant">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <a href={href('/')} className="font-headline font-extrabold text-lg text-primary">
            Piccard
          </a>
          <a href={href('/blog')} className="text-sm text-muted-soft hover:text-on-surface transition-colors">
            {ALL_POSTS_I18N[locale ?? 'en'] ?? ALL_POSTS_I18N.en}
          </a>
        </div>
      </header>

      <main className="flex-1 w-full max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
        <p className="text-xs font-bold uppercase tracking-wider text-muted-soft">
          {locale ? label(locale) : label('en')}
        </p>
        <h1 className="font-headline font-extrabold text-3xl text-on-surface mt-1">{copy.title}</h1>
        <p className="text-muted-soft mt-3">{copy.sub}</p>

        {groups.map(([langLabel, list]) => (
          <section key={langLabel as string} className="mt-10">
            {groups.length > 1 && (
              <h2 className="text-xs font-bold uppercase tracking-wider text-muted-soft">
                {langLabel as string}
              </h2>
            )}
            <div className="mt-3 grid gap-4">
              {(list as Page[]).map((p) => (
                <a
                  key={p.path}
                  href={`${p.path}/`}
                  className="hover-lift rounded-2xl bg-surface p-6 block"
                >
                  <div className="text-xs text-muted-soft">{p.date}</div>
                  <div className="font-bold text-on-surface mt-1">
                    {p.title.replace(' | Piccard', '')}
                  </div>
                  <p className="text-sm text-muted-soft mt-2">{p.description}</p>
                </a>
              ))}
            </div>
          </section>
        ))}

        {/* Cross-cluster nav: the spoke side of the mesh. Locale scope keeps
            readers inside their language (a locale hub only links sibling
            locale hubs that exist). */}
        <nav
          className="mt-14 border-t border-outline-variant pt-6"
          aria-label={locale ? SIBLINGS_I18N[locale] ?? SIBLINGS_I18N.en : SIBLINGS_I18N.en}
        >
          <h2 className="font-headline font-bold text-lg mb-3">
            {locale ? SIBLINGS_I18N[locale] ?? SIBLINGS_I18N.en : SIBLINGS_I18N.en}
          </h2>
          <div className="flex flex-wrap gap-2">
            {CLUSTER_IDS.filter((c) => c !== cluster && (!locale || CLUSTER_LOCALES[c]?.includes(locale))).map(
              (c) => (
                <a
                  key={c}
                  href={href(`/blog/c/${c}`)}
                  className="rounded-full border border-outline-variant px-4 py-1.5 text-sm text-on-surface hover:border-primary hover:text-primary transition-colors"
                >
                  {locale ? CLUSTER_LABEL[c]?.[locale] ?? CLUSTER_LABEL[c].en : CLUSTER_LABEL[c].en}
                </a>
              ),
            )}
          </div>
        </nav>
      </main>

      <Footer />
    </div>
  )
}

const SIBLINGS_I18N: Record<string, string> = {
  en: 'More topics',
  ko: '다른 주제',
  ja: '他のトピック',
  'zh-TW': '其他主題',
}
